import { useMutation } from '@apollo/client';
import { gql } from '__generated__/gql';
import { UnitTransferRequestStatusEnumType } from '__generated__/graphql';
import { FC } from 'react';
import { useNavigate } from 'react-router-dom';

import ConfirmButton from 'primitives/ConfirmButton';

import SectionCard from 'components/SectionCard';

import OverrideJointDematWarningButton from './OverrideJointDematWarningButton';
import RejectUnitTransferRequestButton from './RejectUnitTransferRequestButton';

const START_UNIT_TRANSFER_REVIEW_MUTATION = gql(`
  mutation StartUnitTransferReview($id: ID!) {
    startUnitTransferReview(id: $id) {
      id
      status
    }
  }
`);

const ISSUE_UNIT_TRANSFER_CONDITIONS_MUTATION = gql(`
  mutation IssueUnitTransferConditions($id: ID!) {
    issueUnitTransferConditions(id: $id) {
      id
      status
      conditionsIssuedAt
    }
  }
`);

const REQUEST_VALUATION_REPORT_MUTATION = gql(`
  mutation RequestValuationReport($id: ID!) {
    requestValuationReport(id: $id) {
      id
      valuationReportRequested
    }
  }
`);

const DELETE_UNIT_TRANSFER_REQUEST_MUTATION = gql(`
  mutation DeleteUnitTransferRequest($id: ID!) {
    deleteUnitTransferRequest(id: $id)
  }
`);

const TERMINAL_STATUSES = [
  UnitTransferRequestStatusEnumType.Executed,
  UnitTransferRequestStatusEnumType.ReadyForDis,
  UnitTransferRequestStatusEnumType.Rejected,
  UnitTransferRequestStatusEnumType.Withdrawn,
  UnitTransferRequestStatusEnumType.TransfereeDeclined,
];

/**
 * Where the next step lives when it is not a button on this screen — the Documents and
 * Signatures tabs own their own gates, and ops was previously left to guess which tab to open.
 */
const NEXT_STEP_HINTS: Partial<Record<UnitTransferRequestStatusEnumType, string>> = {
  [UnitTransferRequestStatusEnumType.Draft]:
    'Waiting on the transferor to submit this request. Nothing to do here yet.',
  [UnitTransferRequestStatusEnumType.PendingTransfereeAcknowledgement]:
    'Waiting on the transferee to accept or decline.',
  [UnitTransferRequestStatusEnumType.PendingEscrowException]:
    'Waiting on ops and legal sign-off in the escrow exception panel below.',
  [UnitTransferRequestStatusEnumType.ConditionsIssued]:
    'Waiting on the transferor to upload their documents.',
  [UnitTransferRequestStatusEnumType.PendingDocuments]:
    'Waiting on the transferor to upload their documents.',
  [UnitTransferRequestStatusEnumType.DocumentsIncomplete]:
    'Sent back to the transferor — waiting on re-uploads.',
  [UnitTransferRequestStatusEnumType.DocumentsUnderReview]:
    'Next: review each document and mark documents complete on the Documents tab.',
  [UnitTransferRequestStatusEnumType.DocumentsVerified]:
    'Next: initiate signing on the Signatures tab.',
  [UnitTransferRequestStatusEnumType.PendingSignatures]:
    'Next: send each signature request in sequence on the Signatures tab.',
  [UnitTransferRequestStatusEnumType.Executed]:
    'Executed. The remaining depository steps are handled offline.',
  [UnitTransferRequestStatusEnumType.ReadyForDis]:
    'Ready for DIS. The remaining depository steps are handled offline.',
};

const UnitTransferActions: FC<{
  requestId: string;
  status: UnitTransferRequestStatusEnumType;
  valuationReportRequested: boolean;
}> = ({ requestId, status, valuationReportRequested }) => {
  const [startUnitTransferReview, { loading: startingReview, error: startReviewError }] =
    useMutation(START_UNIT_TRANSFER_REVIEW_MUTATION);
  const [issueUnitTransferConditions, { loading: issuingConditions, error: issueConditionsError }] =
    useMutation(ISSUE_UNIT_TRANSFER_CONDITIONS_MUTATION);
  const [
    requestValuationReport,
    { loading: requestingValuationReport, error: valuationReportError },
  ] = useMutation(REQUEST_VALUATION_REPORT_MUTATION);

  const navigate = useNavigate();
  const [deleteUnitTransferRequest, { loading: deleting, error: deleteError }] = useMutation(
    DELETE_UNIT_TRANSFER_REQUEST_MUTATION
  );

  const isWithdrawn = status === UnitTransferRequestStatusEnumType.Withdrawn;
  const isTerminal = TERMINAL_STATUSES.includes(status);

  // Exactly one status-appropriate primary action, instead of five buttons in an
  // undifferentiated wrap row.
  const primaryAction = (() => {
    if (status === UnitTransferRequestStatusEnumType.Submitted)
      return (
        <ConfirmButton
          loading={startingReview}
          error={startReviewError}
          description="Start reviewing this unit transfer request?"
          onConfirm={() => startUnitTransferReview({ variables: { id: requestId } })}
        >
          Start Review
        </ConfirmButton>
      );

    if (status === UnitTransferRequestStatusEnumType.UnderReview)
      return (
        <ConfirmButton
          loading={issuingConditions}
          error={issueConditionsError}
          description="Issue conditions for this transfer? This generates the document checklist the transferor must satisfy."
          onConfirm={() => issueUnitTransferConditions({ variables: { id: requestId } })}
        >
          Issue Conditions
        </ConfirmButton>
      );

    return null;
  })();

  const hint = NEXT_STEP_HINTS[status];

  return (
    <SectionCard title="Actions" headerContent={primaryAction}>
      <div className="flex flex-col gap-3">
        {!primaryAction && hint ? <p className="text-sm text-gray-600">{hint}</p> : null}

        {/* Valuation report and the joint-demat override used to render on executed and
            rejected requests too, offering ops actions that can no longer mean anything. */}
        {!isTerminal ? (
          <div className="flex flex-row flex-wrap gap-2">
            <ConfirmButton
              variant="secondary"
              loading={requestingValuationReport}
              error={valuationReportError}
              disabled={valuationReportRequested}
              description="Request a valuation report for this transfer? It is added to the transferor's document checklist as a required upload."
              onConfirm={() => requestValuationReport({ variables: { id: requestId } })}
            >
              Request Valuation Report
            </ConfirmButton>
            <OverrideJointDematWarningButton id={requestId} />
            <RejectUnitTransferRequestButton id={requestId} />
          </div>
        ) : (
          <p className="text-sm text-gray-500">
            This request is closed — no further actions are available.
          </p>
        )}

        {isWithdrawn ? (
          <div>
            <ConfirmButton
              variant="secondary"
              loading={deleting}
              error={deleteError}
              title="Delete withdrawn request"
              confirmButtonLabel="Delete"
              description="Permanently delete this withdrawn request along with its schemes, documents, signature requests and timeline? This cannot be undone."
              onConfirm={() =>
                deleteUnitTransferRequest({
                  variables: { id: requestId },
                  update: cache => {
                    cache.evict({
                      id: cache.identify({ __typename: 'UnitTransferRequestType', id: requestId }),
                    });
                    cache.gc();
                  },
                }).then(() => navigate('/unit-transfers'))
              }
            >
              Delete Request
            </ConfirmButton>
          </div>
        ) : null}
      </div>
    </SectionCard>
  );
};

export default UnitTransferActions;
